/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { Ban, CircleCheck, FilePlus, FileUp, RefreshCw, Send, ShieldCheck, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, isApiError } from '../../api';
import {
  CONSENT_METHODS,
  type ConsentSide,
  type EnterpriseCodeCheck,
  type HolderDomain,
  type NameChangeCheck,
  type NameChangeChecklistItem,
  type NameChangeDeclaration,
  type NameChangeDetail,
  type NameChangeDocument,
  type NameChangeEvent,
  type ReviewDecisionNc,
} from '../../api/areas/namechange';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { CustomerScope } from '../../components/NotifyCustomer';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDate, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { NameChangeStatusBadge } from './NameChangesPage';
import { PanelExpected, PanelOperationActions, PanelStatusBadge } from './panel';
import { alternativesOf, missingName } from '../../lib/requirementNames';
import { useDocName, useEnum, useLocalName } from './shared';

const MIME = ['application/pdf', 'image/jpeg', 'image/png', 'application/xml', 'text/xml'];

/** The file as base64 (without the `data:` prefix). */
function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsDataURL(file);
  });
}

function UploadForm({ close, changed, d }: DialogBodyProps & { d: NameChangeDetail }) {
  const { t } = useTranslation('services');
  const docName = useDocName();
  const status = d.request.status;
  // The declaration under the new name is generated per domain, never uploaded here.
  const allowed = d.checklist.filter(
    (c) => c.source !== 'DOMAIN_DECLARATION' && (c.stage === 'BEFORE_APPROVAL' ? ['DRAFT', 'CORRECTION_REQUESTED'].includes(status) : ['DRAFT', 'CORRECTION_REQUESTED', 'SUBMITTED', 'APPROVED'].includes(status)),
  );
  const [code, setCode] = useState(allowed.find((c) => !c.present)?.code ?? allowed[0]?.code ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const w = useWrite(async (_: null, opts) => {
    if (!file) throw new Error('no file');
    const content_base64 = await readBase64(file);
    return api.uploadNameChangeDocument(d.request.id, { document_code: code, mime_type: file.type, file_name: file.name, content_base64, reason: reason.trim() }, opts);
  });
  const done = w.state.phase === 'done';
  const badType = file !== null && !MIME.includes(file.type);
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('nameChange.uploaded')}</Callout>
      ) : (
        <>
          <Field label={t('nameChange.documentType')}>
            <select className={selectClass} value={code} onChange={(ev) => setCode(ev.target.value)}>
              {allowed.map((c) => (
                <option key={c.code} value={c.code}>
                  {docName(c)}
                  {c.template_ref ? ` (${c.template_ref})` : ''}
                  {c.present ? ' ✓' : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('nameChange.file')} hint={t('nameChange.fileHint')}>
            <input type="file" accept={MIME.join(',')} className="text-sm" onChange={(ev) => setFile(ev.target.files?.[0] ?? null)} />
          </Field>
          {badType ? <Callout tone="warn">{t('nameChange.fileType')}</Callout> : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!code || !file || badType || !reason.trim()} submit={done ? undefined : t('nameChange.upload')} onClose={close} />
    </form>
  );
}

function ConsentForm({ close, changed, id }: DialogBodyProps & { id: string }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  const [side, setSide] = useState<ConsentSide>('CURRENT_REGISTRANT');
  const [method, setMethod] = useState<string>('SIGNED_DOCUMENT');
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.recordNameChangeConsent(id, { side, method, reason: reason.trim() }, opts));
  const result = w.state.phase === 'done' ? w.state.result : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {result ? (
        <Callout tone="ok">{result.both_confirmed ? t('nameChange.consentBoth') : t('nameChange.consentOne')}</Callout>
      ) : (
        <>
          <Field label={t('nameChange.side')}>
            <select className={selectClass} value={side} onChange={(ev) => setSide(ev.target.value as ConsentSide)}>
              {(['CURRENT_REGISTRANT', 'NEW_REGISTRANT'] as const).map((s) => (
                <option key={s} value={s}>
                  {e('consentSide', s)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('nameChange.method')}>
            <select className={selectClass} value={method} onChange={(ev) => setMethod(ev.target.value)}>
              {CONSENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {e('consentMethod', m)}
                </option>
              ))}
            </select>
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={result ? undefined : t('nameChange.recordConsent')} onClose={close} />
    </form>
  );
}

function ReviewForm({ close, changed, d }: DialogBodyProps & { d: NameChangeDetail }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  const failing = d.request.checks.filter((c) => c.status === 'FAIL');
  const [decision, setDecision] = useState<ReviewDecisionNc>(failing.length ? 'REQUEST_CORRECTION' : 'APPROVE');
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.reviewNameChange(d.request.id, { decision, reason: reason.trim() }, opts));
  const result = w.state.phase === 'done' ? w.state.result : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {result ? (
        <Callout tone="ok">{t('nameChange.reviewed', { status: e('ncStatus', result.status) })}</Callout>
      ) : (
        <>
          {failing.length ? <Callout tone="warn">{t('nameChange.failingChecks', { checks: failing.map((c) => e('ncCheck', c.code)).join(', ') })}</Callout> : null}
          <Field label={t('nameChange.decision')}>
            <select className={selectClass} value={decision} onChange={(ev) => setDecision(ev.target.value as ReviewDecisionNc)}>
              {(['APPROVE', 'REQUEST_CORRECTION', 'REJECT'] as const).map((x) => (
                <option key={x} value={x} disabled={x === 'APPROVE' && failing.length > 0}>
                  {e('ncDecision', x)}
                </option>
              ))}
            </select>
          </Field>
          {decision === 'REJECT' ? <Callout tone="crit">{t('nameChange.rejectWarning')}</Callout> : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={result ? undefined : t('nameChange.decide')} onClose={close} danger={decision === 'REJECT'} />
    </form>
  );
}

/** "Xác nhận hoàn tất": VNNIC handled it offline; domain:info decides. */
function ConfirmCompletionForm({ close, changed, id }: DialogBodyProps & { id: string }) {
  const { t } = useTranslation('services');
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.confirmNameChangeCompletion(id, { reason: reason.trim() }, opts));
  const done = w.state.phase === 'done';
  const err = w.state.phase === 'error' ? w.state.error : null;
  const notYet = isApiError(err) && err.code === 'REGISTRY_NOT_UPDATED' ? (err.details as { registry_registrant?: string | null; expected_registrant?: string | null } | undefined) : undefined;
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      <Callout tone="info">{t('nameChange.confirmHint')}</Callout>
      {done ? <Callout tone="ok">{t('nameChange.confirmed')}</Callout> : <ReasonField value={reason} onChange={setReason} />}
      {notYet ? (
        <Callout tone="warn">{t('nameChange.notYet', { actual: notYet.registry_registrant ?? '—', expected: notYet.expected_registrant ?? '—' })}</Callout>
      ) : err ? (
        <WriteError error={err} />
      ) : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={done ? undefined : t('nameChange.confirm')} onClose={close} />
    </form>
  );
}

const CHECK_TONE = { PASS: 'ok', FAIL: 'crit', WARN: 'warn' } as const;
const EC_TONE = { MATCH: 'ok', MISMATCH: 'crit', NOT_APPLICABLE: 'warn' } as const;
const DECL_TONE = { GENERATED: 'warn', UPLOADED: 'info', VERIFIED: 'ok', REVIEW_REQUIRED: 'warn', APPROVED: 'ok', REJECTED: 'crit' } as const;

/** The enterprise-code comparison (masked hints only). */
function EnterpriseCode({ ec }: { ec: EnterpriseCodeCheck }) {
  const { t } = useTranslation('services');
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone={EC_TONE[ec.status]}>{t(`nameChange.ec.${ec.status}`)}</Badge>
      {ec.current_hint || ec.new_hint ? <span className="font-mono text-xs">{t('nameChange.ecHints', { current: ec.current_hint ?? '—', next: ec.new_hint ?? '—' })}</span> : null}
    </span>
  );
}

/** `/name-changes/:id`: one request — case, checklist of its requirement-set version, checks, timeline, actions. */
export function NameChangePage() {
  const { id = '' } = useParams();
  const { t } = useTranslation('services');
  const e = useEnum();
  const name = useLocalName();
  const docName = useDocName();
  const result = useApi((signal) => api.getNameChange(id, { signal }), [id]);
  useDocumentTitle(result.status === 'success' ? `${result.data.domain} · ${t('nameChange.title')}` : t('nameChange.title'));
  if (result.status === 'loading') return <Loading rows={6} />;
  if (result.status === 'error') return <DataError error={result.error} title={t('common.loadFailed')} onRetry={result.reload} />;
  const d = result.data;
  const r = d.request;
  const reload = result.reload;
  const st = r.status;
  const editable = st === 'DRAFT' || st === 'CORRECTION_REQUESTED';
  const canUpload = editable || st === 'SUBMITTED' || st === 'APPROVED';
  const cancellable = editable || st === 'SUBMITTED' || st === 'APPROVED';
  const missing = (entries: string[]) => entries.map((m) => missingName(m, d.checklist, docName, t('nameChange.or'))).join('; ');
  const checklistCols: Column<NameChangeChecklistItem>[] = [
    {
      key: 'doc',
      header: t('nameChange.col.document'),
      cell: (c) => (
        <span className="grid gap-0.5">
          <span className="font-medium">{docName(c)}</span>
          <span className="font-mono text-xs text-muted-foreground">{c.code}</span>
        </span>
      ),
    },
    { key: 'stage', header: t('nameChange.col.stage'), cell: (c) => e('ncStage', c.stage) },
    { key: 'req', header: t('nameChange.col.required'), cell: (c) => (c.required ? (c.one_of ? <span title={c.one_of}>{t('nameChange.oneOf', { others: alternativesOf(c, d.checklist, docName) })}</span> : t('common.yes')) : t('common.no')) },
    {
      key: 'tpl',
      header: t('nameChange.col.template'),
      cell: (c) => (
        <span className="grid gap-0.5 text-xs">
          <span>{c.template_ref ?? '—'}</span>
          {c.source === 'DOMAIN_DECLARATION' ? <span className="text-muted-foreground">{t('nameChange.generatedPerDomain')}</span> : null}
        </span>
      ),
    },
    { key: 'sign', header: t('nameChange.col.signedBy'), cell: (c) => <span className="text-xs">{c.signed_by ? e('signedBy', c.signed_by) : '—'}</span> },
    { key: 'present', header: t('nameChange.col.present'), cell: (c) => (c.present ? <Badge tone="ok">{t('nameChange.present', { count: c.uploads })}</Badge> : <Badge tone={c.required ? 'warn' : 'neutral'}>{t('nameChange.absent')}</Badge>) },
  ];
  const checkCols: Column<NameChangeCheck>[] = [
    { key: 'code', header: t('nameChange.col.check'), cell: (c) => e('ncCheck', c.code) },
    { key: 'status', header: t('nameChange.col.result'), cell: (c) => <Badge tone={CHECK_TONE[c.status]}>{e('checkStatus', c.status)}</Badge> },
    { key: 'blocking', header: t('nameChange.col.blocking'), cell: (c) => (c.blocking ? t('common.yes') : t('common.no')) },
    { key: 'detail', header: t('nameChange.col.detail'), cell: (c) => <span className="text-xs">{c.detail ?? '—'}</span> },
  ];
  const docCols: Column<NameChangeDocument>[] = [
    { key: 'code', header: t('nameChange.col.document'), cell: (x) => <span className="font-mono text-xs">{x.document_code}</span> },
    { key: 'file', header: t('nameChange.col.file'), cell: (x) => <span className="text-xs">{x.file_name ?? '—'} · {x.mime_type} · {Math.ceil(x.size_bytes / 1024)} KB</span> },
    { key: 'by', header: t('nameChange.col.by'), cell: (x) => <span className="text-xs">{x.uploaded_by_type}</span> },
    { key: 'at', header: t('nameChange.col.at'), className: 'whitespace-nowrap text-xs', cell: (x) => formatDateTime(x.uploaded_at) },
  ];
  const eventCols: Column<NameChangeEvent>[] = [
    { key: 'at', header: t('nameChange.col.at'), className: 'whitespace-nowrap text-xs', cell: (x) => formatDateTime(x.occurred_at) },
    {
      key: 'what',
      header: t('nameChange.col.event'),
      cell: (x) => (
        <span className="text-sm">
          {e('ncEvent', x.event_type)}
          {x.to_status ? ` → ${e('ncStatus', x.to_status)}` : ''}
        </span>
      ),
    },
    { key: 'by', header: t('nameChange.col.by'), cell: (x) => <span className="text-xs">{x.actor_type}</span> },
    { key: 'note', header: t('nameChange.col.note'), cell: (x) => <span className="text-xs">{x.note ?? (Object.keys(x.detail).length ? JSON.stringify(x.detail) : '—')}</span> },
  ];
  const perDomain = d.checklist.some((c) => c.source === 'DOMAIN_DECLARATION');
  const declCols: Column<NameChangeDeclaration>[] = [
    { key: 'domain', header: t('nameChange.col2.domain'), cell: (x) => <span className="font-mono text-xs">{x.domains.join(', ')}</span> },
    { key: 'form', header: t('nameChange.col2.form'), cell: (x) => <span className="text-xs">{x.template_code}</span> },
    { key: 'status', header: t('nameChange.col2.status'), cell: (x) => <Badge tone={DECL_TONE[x.status]}>{t(`nameChange.declStatus.${x.status}`)}</Badge> },
    { key: 'at', header: t('nameChange.col2.generated'), className: 'whitespace-nowrap text-xs', cell: (x) => formatDateTime(x.generated_at) },
  ];
  const holderCols: Column<HolderDomain>[] = [
    {
      key: 'domain',
      header: t('nameChange.col2.domain'),
      cell: (h) => (
        <Link to={`/domains/${encodeURIComponent(h.domain)}`} className="font-medium text-primary hover:underline">
          {h.domain}
        </Link>
      ),
    },
    {
      key: 'request',
      header: t('nameChange.col2.request'),
      cell: (h) =>
        h.request_id === r.id ? (
          <span className="text-xs text-muted-foreground">{t('nameChange.thisRequest')}</span>
        ) : h.request_id && h.status ? (
          <Link to={`/name-changes/${h.request_id}`} className="text-primary hover:underline">
            <NameChangeStatusBadge status={h.status} />
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">{t('nameChange.noRequest')}</span>
        ),
    },
    {
      key: 'decl',
      header: t('nameChange.col2.declaration'),
      cell: (h) => (h.declaration_status ? <Badge tone={DECL_TONE[h.declaration_status]}>{t(`nameChange.declStatus.${h.declaration_status}`)}</Badge> : <span className="text-xs text-muted-foreground">—</span>),
    },
  ];
  const consent = (k: 'current_registrant' | 'new_registrant') => {
    const c = r.consent[k];
    return c ? `${e('consentMethod', c.method)} · ${formatDateTime(c.confirmed_at)}` : t('nameChange.waiting');
  };
  return (
    <CustomerScope customerId={d.customer_id}>
      <PageHeader
        title={`${e('ncCase', r.case_code)} · ${d.domain}`}
        subtitle={r.sub_case ? e('ncSubCase', r.sub_case) : t('nameChange.subtitle')}
        crumbs={[{ label: t('crumbs.business') }, { label: t('nameChange.title'), to: '/name-changes' }]}
        actions={
          <>
            {canUpload ? (
              <ActionDialog label={t('nameChange.upload')} icon={FileUp} title={t('nameChange.uploadTitle')} onChanged={reload}>
                {(p) => <UploadForm {...p} d={d} />}
              </ActionDialog>
            ) : null}
            {perDomain && editable ? (
              <ReasonAction
                label={t('nameChange.generateDeclaration')}
                icon={FilePlus}
                title={t('nameChange.generateDeclarationTitle')}
                description={t('nameChange.generateDeclarationHint', { domain: d.domain })}
                run={(reason, opts) => api.nameChangeDeclaration(r.id, { reason }, opts)}
                done={() => t('nameChange.declarationGenerated')}
                onChanged={reload}
              />
            ) : null}
            {r.case_code === 'HOLDER_TRANSFER' && (editable || st === 'SUBMITTED') ? (
              <ActionDialog label={t('nameChange.recordConsent')} icon={UserCheck} title={t('nameChange.consentTitle')} onChanged={reload}>
                {(p) => <ConsentForm {...p} id={r.id} />}
              </ActionDialog>
            ) : null}
            {editable ? (
              <ReasonAction
                label={t('nameChange.submit')}
                icon={Send}
                variant="default"
                title={t('nameChange.submitTitle')}
                description={t('nameChange.submitHint')}
                disabled={d.missing_before_approval.length > 0}
                run={(reason, opts) => api.submitNameChange(r.id, { reason }, opts)}
                done={(x) => t('nameChange.submitted', { due: formatDate(x.review_due_at) })}
                onChanged={reload}
              />
            ) : null}
            {st === 'SUBMITTED' ? (
              <ActionDialog label={t('nameChange.review')} icon={CircleCheck} variant="default" title={t('nameChange.reviewTitle')} onChanged={reload}>
                {(p) => <ReviewForm {...p} d={d} />}
              </ActionDialog>
            ) : null}
            {st === 'PANEL_PENDING' ? (
              <ActionDialog label={t('nameChange.confirm')} icon={ShieldCheck} variant="default" title={t('nameChange.confirmTitle')} onChanged={reload}>
                {(p) => <ConfirmCompletionForm {...p} id={r.id} />}
              </ActionDialog>
            ) : null}
            {st === 'PANEL_PENDING' || st === 'REGISTRY_CONTACT' ? (
              <ReasonAction
                label={t('nameChange.vnnicRefused')}
                icon={Ban}
                danger
                title={t('nameChange.vnnicRefusedTitle')}
                warning={t('nameChange.vnnicRefusedWarning')}
                run={(reason, opts) => api.recordVnnicRefusal(r.id, { reason }, opts)}
                done={() => t('nameChange.closed')}
                onChanged={reload}
              />
            ) : null}
            {cancellable ? (
              <ReasonAction
                label={t('nameChange.cancel')}
                danger
                variant="ghost"
                title={t('nameChange.cancelTitle')}
                warning={t('nameChange.cancelWarning')}
                run={(reason, opts) => api.cancelNameChange(r.id, { reason }, opts)}
                done={() => t('nameChange.closed')}
                onChanged={reload}
              />
            ) : null}
            <Button type="button" size="sm" variant="ghost" onClick={reload}>
              <RefreshCw /> {t('common.refresh')}
            </Button>
          </>
        }
      />
      <div className="flex flex-col gap-6">
        {st === 'APPROVED' && d.missing_after_approval.length ? <Callout tone="warn">{t('nameChange.waitingAfter', { docs: missing(d.missing_after_approval) })}</Callout> : null}
        {editable && d.missing_before_approval.length ? <Callout tone="info">{t('nameChange.waitingBefore', { docs: missing(d.missing_before_approval) })}</Callout> : null}
        {d.enterprise_code?.status === 'MISMATCH' ? <Callout tone="crit">{t('nameChange.ecMismatch')}</Callout> : null}
        {d.enterprise_code?.status === 'NOT_APPLICABLE' ? <Callout tone="warn">{t('nameChange.ecNotApplicable')}</Callout> : null}
        {st === 'PANEL_PENDING' ? (
          <Callout tone="warn">
            {t('nameChange.panelHint', { handle: r.new_contact_handle ?? '—' })}
            {r.new_admin_contact_handle ? ` ${t('nameChange.panelHintAdmin', { handle: r.new_admin_contact_handle })}` : ''}
          </Callout>
        ) : null}
        <Section title={t('nameChange.summary')}>
          <KeyValue
            columns={3}
            rows={[
              [t('nameChange.col.status'), <NameChangeStatusBadge key="s" status={st} />],
              [
                t('nameChange.col.domain'),
                <Link key="d" to={`/domains/${encodeURIComponent(d.domain)}`} className="font-medium text-primary hover:underline">
                  {d.domain}
                </Link>,
              ],
              [t('nameChange.case'), `${e('ncCase', r.case_code)}${r.sub_case ? ` · ${e('ncSubCase', r.sub_case)}` : ''}`],
              [t('nameChange.fromHolder'), d.from_holder_name ?? '—'],
              [
                t('nameChange.toHolder'),
                <Link key="to" to={`/subjects/${encodeURIComponent(r.to_subject_id)}`} className="text-primary hover:underline">
                  {d.to_holder_name ?? r.to_subject_id}
                </Link>,
              ],
              [
                t('nameChange.requirementSet'),
                `${name({ name_vi: d.requirement_set.title_vi, name_en: d.requirement_set.title_en })} · v${d.requirement_set.version}${d.requirement_set.latest_version > d.requirement_set.version ? ` ${t('nameChange.newerVersion', { version: d.requirement_set.latest_version })}` : ''}`,
              ],
              [t('nameChange.dueAt'), r.review_due_at ? formatDateTime(r.review_due_at) : '—'],
              d.enterprise_code ? [t('nameChange.enterpriseCode'), <EnterpriseCode key="ec" ec={d.enterprise_code} />] : null,
              r.spelling_correction_reason ? [t('nameChange.spellingMarked'), r.spelling_correction_reason] : null,
              r.case_code === 'HOLDER_TRANSFER' ? [t('nameChange.consentCurrent'), consent('current_registrant')] : null,
              r.case_code === 'HOLDER_TRANSFER' ? [t('nameChange.consentNew'), consent('new_registrant')] : null,
              [t('nameChange.addon'), d.addon ? `${name(d.addon)} · ${e('addonState', d.addon.state)}` : '—'],
              [t('nameChange.newContact'), r.new_contact_handle ? <span key="h" className="font-mono text-xs">{r.new_contact_handle}</span> : '—'],
              r.new_admin_contact_handle ? [t('nameChange.adminContact'), <span key="a" className="font-mono text-xs">{r.new_admin_contact_handle}</span>] : null,
              r.completed_via ? [t('nameChange.completedVia'), t(`nameChange.via.${r.completed_via}`)] : null,
              r.completion_lock_status
                ? [
                    t('nameChange.lockStatus'),
                    <Badge key="l" tone={r.completion_lock_status === 'APPLIED' ? 'ok' : r.completion_lock_status === 'REJECTED' ? 'crit' : 'warn'}>
                      {t(`nameChange.lock.${r.completion_lock_status}`)}
                    </Badge>,
                  ]
                : null,
              r.review_reason ? [t('nameChange.reviewReason'), r.review_reason] : null,
              r.closed_reason ? [t('nameChange.closedReason'), r.closed_reason] : null,
              r.note ? [t('nameChange.note'), r.note] : null,
              [t('nameChange.legalBasis'), <span key="lb" className="text-xs">{d.requirement_set.legal_basis}</span>],
            ]}
          />
        </Section>
        {d.panel_operation ? (
          <Section title={t('panel.stepTitle')} description={t('panel.stepHint')} actions={<PanelOperationActions op={d.panel_operation} onChanged={reload} />}>
            <KeyValue
              columns={3}
              rows={[
                [t('panel.col.status'), <PanelStatusBadge key="p" status={d.panel_operation.status} />],
                [t('panel.col.expected'), <PanelExpected key="x" op={d.panel_operation} />],
                [t('panel.executor'), e('panelExecutor', d.panel_operation.executor)],
                [t('panel.col.reference'), d.panel_operation.submitted_reference ?? '—'],
                [t('panel.submittedAt'), formatDateTime(d.panel_operation.submitted_at)],
                [t('panel.lastChecked'), formatDateTime(d.panel_operation.last_checked_at)],
                d.panel_operation.observed ? [t('panel.observed'), <span key="o" className="font-mono text-xs">{d.panel_operation.observed.registrant ?? (d.panel_operation.observed.statuses ?? []).join(', ')}</span>] : null,
              ]}
            />
          </Section>
        ) : null}
        <Section title={t('nameChange.checklist', { version: d.requirement_set.version })} flush>
          <DataTable columns={checklistCols} rows={d.checklist} rowKey={(c) => c.code} empty={<EmptyState title={t('nameChange.empty')} />} />
        </Section>
        {perDomain ? (
          <Section
            title={t('nameChange.declarations')}
            description={t('nameChange.declarationsHint')}
            actions={
              <Link to={`/subjects/${encodeURIComponent(r.to_subject_id)}`} className="text-sm font-medium text-primary hover:underline">
                {t('nameChange.openHolder')}
              </Link>
            }
            flush
          >
            <DataTable columns={declCols} rows={d.declarations ?? []} rowKey={(x) => x.declaration_id} empty={<EmptyState title={t('nameChange.noDeclarations')} />} />
          </Section>
        ) : null}
        {perDomain && (d.holder_domains?.length ?? 0) > 1 ? (
          <Section title={t('nameChange.holderDomains')} description={t('nameChange.holderDomainsHint')} flush>
            <DataTable columns={holderCols} rows={d.holder_domains ?? []} rowKey={(h) => h.domain_id} />
          </Section>
        ) : null}
        <Section title={t('nameChange.checks')} description={r.checks_at ? t('nameChange.checksAt', { at: formatDateTime(r.checks_at) }) : undefined} flush>
          <DataTable columns={checkCols} rows={r.checks} rowKey={(c) => c.code} empty={<EmptyState title={t('nameChange.noChecks')} />} />
        </Section>
        <Section title={t('nameChange.documents')} flush>
          <DataTable columns={docCols} rows={d.documents} rowKey={(x) => x.id} empty={<EmptyState title={t('nameChange.noDocuments')} />} />
        </Section>
        <Section title={t('nameChange.timeline')} flush>
          <DataTable columns={eventCols} rows={d.timeline} rowKey={(x) => x.id} empty={<EmptyState title={t('nameChange.noEvents')} />} />
        </Section>
      </div>
    </CustomerScope>
  );
}
